import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Select from 'react-select';

import AddPDFFileModal from './AddPDFFileModal';
import { backgroundGrey } from 'lib/css/colors';
import Container from 'components/shared/Container';
import EntityType, { ENTITY_TYPE_OPTIONS } from 'lib/enums/EntityType';
import {
  GLOBAL_VENDORS_FOR_DROPDOWN,
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  PRODUCT_ENRICHMENT_RESOURCES,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import {
  ProductEnrichmentResourcesFileType,
  ProductEnrichmentResourcesFileTypeLabels,
} from 'lib/enums/ProductEnrichmentResourcesFileType';
import { ProductEnrichmentResourcesQAStatusLabels } from 'lib/enums/ProductEnrichmentResourcesQAStatus';
import { ProductTypeDisplay } from 'lib/enums/ProductType';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import { SuperchargedStatusLabels } from 'lib/enums/SuperchargedStatus';
import useGet from 'lib/hooks/useGet';

function PDFUploadView() {
  const [entityType, setEntityType] = useState();
  const [entity, setEntity] = useState({});
  const [qaStatus, setQaStatus] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);
  const history = useHistory();

  const entityNames = useMemo(() => {
    if (entityType?.value === EntityType.DISTRIBUTOR && globalVendors) {
      return globalVendors.map(vendor => ({
        value: vendor.id,
        label: vendor.name,
      }));
    }
    return [];
  }, [entityType, globalVendors]);

  const qaStatusOptions = useMemo(() => {
    return Object.entries(ProductEnrichmentResourcesQAStatusLabels).map(([value, label]) => ({
      value,
      label,
    }));
  }, []);

  const {
    data: { resource_records = [], count = 0 } = {},
    loading,
    refetch,
  } = useGet(PRODUCT_ENRICHMENT_RESOURCES, {
    file_type: ProductEnrichmentResourcesFileType.PDF_FILE,
    ...(entityType?.value === EntityType.DISTRIBUTOR && { global_vendor_id: entity?.value }),
    ...(entityType?.value === EntityType.MANUFACTURER && { global_manufacturer_id: entity?.value }),
    ...(qaStatus && { qa_status: qaStatus.value }),
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
  });

  if (loading) {
    return <LoadingSpinner short />;
  }

  return (
    <Container>
      <Row>
        <Col md={6}>
          <h4 className="mb-3">PDF Upload</h4>
        </Col>
        <Col md={6} className="d-flex justify-content-end">
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            Upload New Spec Sheet
          </Button>
        </Col>
      </Row>

      <Row className="mt-4 mb-3">
        <Col md={3}>
          <Form.Group className="mr-3">
            <Form.Label>Entity Type</Form.Label>
            <Select
              value={entityType}
              options={ENTITY_TYPE_OPTIONS}
              onChange={e => {
                setEntity({});
                setEntityType(e || {});
              }}
              isSearchable
              isClearable
              placeholder="Select Entity Type"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Entity Name</Form.Label>
            {entityType?.value === EntityType.MANUFACTURER ? (
              <SearchableScrollableSelect
                onChange={e => setEntity(e)}
                fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                optionMapper={manufacturer => ({
                  value: manufacturer.id,
                  label: manufacturer.name,
                })}
                autoFocus={false}
                placeholder="Select Manufacturer"
              />
            ) : (
              <Select
                value={entity}
                options={entityNames}
                onChange={e => setEntity(e)}
                isSearchable
                isClearable
                placeholder="Select Distributor"
              />
            )}
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>QA Status</Form.Label>
            <Select
              value={qaStatus}
              options={qaStatusOptions}
              onChange={e => {
                setQaStatus(e);
                setCurrentPage(1);
              }}
              isSearchable
              isClearable
              placeholder="Select QA Status"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mt-3 mb-2">
        <Col className="d-flex justify-content-between align-items-center">
          <span>{count} PDF Upload found</span>
          <div className="d-flex justify-content-end px-3 py-3">
            <PaginationComponent
              totalItems={count}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              disabled={loading}
            />
          </div>
        </Col>
      </Row>

      <Table className={css(styles.tableText)}>
        <thead className={css(styles.thead)}>
          <tr>
            <th>Resource ID</th>
            <th>Created Date</th>
            <th>Entity Type</th>
            <th>Entity Name</th>
            <th>File Type</th>
            <th>Product Count</th>
            <th>Supercharged Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className={css(styles.tbody)}>
          {resource_records.length ? (
            resource_records.map((resource, index) => (
              <tr key={index}>
                <td>{resource.id}</td>
                <td>{resource.date_created}</td>
                <td>
                  {resource.global_manufacturer
                    ? ProductTypeDisplay.MANUFACTURER
                    : ProductTypeDisplay.DISTRIBUTOR}
                </td>
                <td>{resource.global_manufacturer?.name || resource.global_vendor?.name}</td>
                <td>{ProductEnrichmentResourcesFileTypeLabels[resource.file_type]}</td>
                <td>{resource.product_count}</td>
                <td>{SuperchargedStatusLabels[resource.supercharged_status]}</td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() =>
                      history.push(`/product-enrichment-resources/pdf-qa/${resource.id}`)
                    }
                  >
                    QA
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="100%" className="text-center">
                No PDF Uploads found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <AddPDFFileModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        refetch={refetch}
      />
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky-top',
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
});

export default PDFUploadView;
